/** Stroke icons. Fill stays light so the map remains readable. */

import {
  mdiAccount,
  mdiAccountHardHat,
  mdiAirplane,
  mdiCarWash,
  mdiEmoticonPoop,
  mdiEgg,
  mdiFire,
  mdiFlask,
  mdiGrain,
  mdiHelicopter,
  mdiHumanChild,
  mdiQuadcopter,
  mdiTankerTruck,
  mdiToilet,
} from '@mdi/js'

const STROKE = 'currentColor'

function svg(body: string, size = 18): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 18 18" fill="none" aria-hidden="true">${body}</svg>`
}

/** MDI glyphs fill 24² edge-to-edge; our stroke icons leave optical padding in 18². */
function mdiSvg(pathD: string, size = 18): string {
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" aria-hidden="true"><g transform="translate(2.4 2.4) scale(0.8)"><path d="${pathD}" fill="${STROKE}"/></g></svg>`
}

/** Types that get the hazard marker treatment (amber ring). */
export const HAZARD_TYPES = new Set([
  'fire',
  'explosion',
  'vapor_cloud',
  'pipeburst',
  'rupture',
  'gas_leak',
  'leak',
  'hot_zone',
  'isolation',
  'evacuate',
  'shelter',
  'exclusion',
  'release_liquids',
  'tornado',
  'hurricane',
  'brwn_plume',
])

/** Types that get the larger vehicle marker treatment. */
export const VEHICLE_TYPES = new Set([
  'car',
  'truck',
  'engine',
  'ladder',
  'ems',
  'hazmat',
  'rescue',
  'rehab',
  'police',
  'work_vehicle',
  'bulldozer',
  'forestry_bulldozer',
  'backhoe',
  'boat',
  'airplane',
  'helicopter',
  'drone',
  'rollover_vehicle',
  'contractor_response',
  'vacuum_tanker',
  'vac_trucks',
])

export function iconSvg(typeId: string, variant?: string, size = 18): string {
  switch (typeId) {
    case 'fire':
      return mdiSvg(mdiFire, size)
    case 'explosion':
      return svg(
        `<path d="M9 2.4v2.6M9 13v2.6M2.4 9h2.6M13 9h2.6M4.2 4.2l1.8 1.8M12 12l1.8 1.8M13.8 4.2l-1.8 1.8M6 12l-1.8 1.8" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>
         <circle cx="9" cy="9" r="2.4" stroke="${STROKE}" stroke-width="1.4"/>`,
        size,
      )
    case 'pipeburst':
    case 'rupture':
      return svg(
        `<path d="M3 9h4M11 9h4M7 9l1.2-2.4L9.6 9 11 6.8" stroke="${STROKE}" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/><circle cx="9" cy="9" r="1.3" fill="${STROKE}"/>`,
        size,
      )
    case 'vapor_cloud':
      return svg(
        `<path d="M4 11.4c0-1.8 1.2-3 2.8-3.1.5-1.8 2-2.9 3.6-2.4 1.2-1.2 3.2-1.1 4.2.4 1.6.1 2.8 1.4 2.8 3 0 1.7-1.4 2.9-3.2 2.9H6.8C5 12.2 4 11.4 4 11.4z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>`,
        size,
      )
    case 'gas_leak':
    case 'leak':
      return svg(
        `<circle cx="9" cy="10" r="3" stroke="${STROKE}" stroke-width="1.4"/><path d="M9 3v2M5.2 5.2l1.2 1.2M12.8 5.2l-1.2 1.2" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>`,
        size,
      )
    case 'brwn_plume':
      return mdiSvg(mdiEmoticonPoop, size)
    case 'tornado':
      return svg(
        `<path d="M9 2.2c1.6 1.4 2.4 2.8 2.2 4.2-.3 1.6-1.6 2.4-1.6 3.6 0 1 .7 1.6 1.4 2.2M9 2.2C7.4 3.6 6.6 5 6.8 6.4c.3 1.6 1.6 2.4 1.6 3.6 0 1-.7 1.6-1.4 2.2" stroke="${STROKE}" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/><path d="M5.2 15.2h7.6" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>`,
        size,
      )
    case 'hurricane':
      return svg(
        `<path d="M14.2 6.2A5.4 5.4 0 1 0 9.2 14.2" stroke="${STROKE}" stroke-width="1.35" stroke-linecap="round"/><path d="M12.4 8.2A3 3 0 1 0 9.4 11.6" stroke="${STROKE}" stroke-width="1.35" stroke-linecap="round"/><circle cx="9" cy="9" r="1.05" fill="${STROKE}"/>`,
        size,
      )
    case 'hot_zone':
      return svg(
        `<circle cx="9" cy="9" r="3" stroke="${STROKE}" stroke-width="1.4"/><path d="M9 2.5v1.8M9 13.7v1.8M2.5 9h1.8M13.7 9h1.8" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>`,
        size,
      )
    case 'isolation':
      return svg(
        `<circle cx="9" cy="9" r="5.2" stroke="${STROKE}" stroke-width="1.4" stroke-dasharray="2.2 1.8"/><circle cx="9" cy="9" r="2.2" stroke="${STROKE}" stroke-width="1.3"/>`,
        size,
      )
    case 'evacuate':
      return svg(
        `<path d="M4 14V7.2l5-3.4 5 3.4V14" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/><path d="M9 9.5v4.2M7.2 11.4L9 9.5l1.8 1.9" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`,
        size,
      )
    case 'shelter':
      return svg(
        `<path d="M3.4 8.2L9 3.8l5.6 4.4V14H3.4V8.2z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/><path d="M7.2 14v-3.4h3.6V14" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>`,
        size,
      )
    case 'exclusion':
      return svg(
        `<circle cx="9" cy="9" r="5.2" stroke="${STROKE}" stroke-width="1.4"/><path d="M5.6 5.6l6.8 6.8M12.4 5.6l-6.8 6.8" stroke="${STROKE}" stroke-width="1.4" stroke-linecap="round"/>`,
        size,
      )
    case 'release_liquids':
      return mdiSvg(mdiGrain, size)
    case 'pen':
      return svg(
        `<path d="M3.2 13.6L7 6.4l3.6 3.8L14.8 3.4" stroke="${STROKE}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
        size,
      )
    case 'freehand':
    case 'sketch':
      return svg(
        `<path d="M4 13.4l.8-3.2 6.4-6.4 2.4 2.4-6.4 6.4L4 13.4zM10.4 4.6l2.4 2.4" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>`,
        size,
      )
    case 'ruler':
      return svg(
        `<path d="M3.2 11.6L11.6 3.2l3.2 3.2L6.4 14.8 3.2 11.6z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/><path d="M6.2 8.6l1.2-1.2M8 10.4l1.2-1.2M9.8 12.2l1.2-1.2" stroke="${STROKE}" stroke-width="1.2" stroke-linecap="round"/>`,
        size,
      )
    case 'car':
      return svg(
        `<path d="M2.6 11.6V10L4.2 8h2.6l1.2-1.8h3.6L13 8h2.4v3.6H2.6z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <circle cx="5.6" cy="12.6" r="1.15" stroke="${STROKE}" stroke-width="1.2"/>
         <circle cx="12.4" cy="12.6" r="1.15" stroke="${STROKE}" stroke-width="1.2"/>`,
        size,
      )
    case 'work_vehicle':
      return svg(
        `<path d="M2.4 12.2V8.4H6.2V6.4H9V8.4h6.6v3.8H2.4z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <circle cx="5" cy="13.2" r="1.15" stroke="${STROKE}" stroke-width="1.2"/>
         <circle cx="12.6" cy="13.2" r="1.15" stroke="${STROKE}" stroke-width="1.2"/>`,
        size,
      )
    case 'contractor_response':
      return svg(
        `<path d="M2.4 12.6V6.4h7.6l2 2.2h3.6v4H2.4z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M10 8.6 11.6 6.8" stroke="${STROKE}" stroke-width="1.2" stroke-linecap="round"/>
         <circle cx="5.2" cy="13.5" r="1.1" stroke="${STROKE}" stroke-width="1.2"/>
         <circle cx="12.2" cy="13.5" r="1.1" stroke="${STROKE}" stroke-width="1.2"/>`,
        size,
      )
    case 'truck':
      return svg(
        `<path d="M2.2 12.4V7.6h9.2V5.8H15.8v6.6H2.2z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <circle cx="5" cy="13.4" r="1.15" stroke="${STROKE}" stroke-width="1.2"/>
         <circle cx="13.2" cy="13.4" r="1.15" stroke="${STROKE}" stroke-width="1.2"/>`,
        size,
      )
    case 'engine':
      return svg(
        `<path d="M2.2 12.6V8.8h7.2V6.6H15.8v6H2.2z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M10.4 6.6V5h3.6V6.6" stroke="${STROKE}" stroke-width="1.25" stroke-linecap="round"/>
         <path d="M3 6.6h5.6M3 7.8h5.6M4.2 6.6v1.2M6.2 6.6v1.2" stroke="${STROKE}" stroke-width="1.2" stroke-linecap="round"/>
         <circle cx="5" cy="13.5" r="1.1" stroke="${STROKE}" stroke-width="1.2"/>
         <circle cx="13" cy="13.5" r="1.1" stroke="${STROKE}" stroke-width="1.2"/>`,
        size,
      )
    case 'ladder':
      return svg(
        `<path d="M2.4 12.6V9h12.8v3.6H2.4zM10.2 9V7.2h4.2V9" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M7.6 8.4 3.2 3.2M9 8.8 4.8 3.8M4.6 5.2l2.6.8M6.2 7l2.4.8" stroke="${STROKE}" stroke-width="1.25" stroke-linecap="round"/>
         <circle cx="5.2" cy="13.5" r="1.1" stroke="${STROKE}" stroke-width="1.2"/>
         <circle cx="12.6" cy="13.5" r="1.1" stroke="${STROKE}" stroke-width="1.2"/>`,
        size,
      )
    case 'rescue':
      return mdiSvg(mdiCarWash, size)
    case 'vacuum_tanker':
      return mdiSvg(mdiTankerTruck, size)
    case 'vac_trucks':
      return svg(
        `<path d="M1.8 12.6V8.4h3.2V6.6H6.8V12.6" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M6.8 12.6V7.4h6.4V12.6" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M13.2 8.6h1.1c1.5 0 2.4.7 2.4 1.7 0 1.3-.9 2.1-2.2 2.1H13.2" stroke="${STROKE}" stroke-width="1.25" stroke-linecap="round"/>
         <circle cx="4" cy="13.6" r="1.05" stroke="${STROKE}" stroke-width="1.15"/>
         <circle cx="11" cy="13.6" r="1.05" stroke="${STROKE}" stroke-width="1.15"/>`,
        size,
      )
    case 'rehab':
      return svg(
        `<path d="M3.4 13.8V8.4M14.6 13.8V8.4" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>
         <path d="M3.4 8.4Q9 4 14.6 8.4" stroke="${STROKE}" stroke-width="1.35" stroke-linecap="round"/>
         <path d="M4.4 9.6Q9 6.4 13.6 9.6" stroke="${STROKE}" stroke-width="1.15" stroke-linecap="round"/>`,
        size,
      )
    case 'hazmat':
      return mdiSvg(mdiFlask, size)
    case 'bulldozer':
      return svg(
        `<path d="M2.2 8.4v5.2M2.2 8.4H4.4" stroke="${STROKE}" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/>
         <path d="M4.2 11.4V8.2H10V6.6H12.2V11.4" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M3.6 12.2h8.2a1.6 1.6 0 010 3.2H3.6a1.6 1.6 0 010-3.2z" stroke="${STROKE}" stroke-width="1.25"/>`,
        size,
      )
    case 'forestry_bulldozer':
      return svg(
        `<path d="M1.5 7.4v6.4M1.5 7.4h1.7" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
         <path d="M3.1 11.6V8.8h4.4v2.8" stroke="${STROKE}" stroke-width="1.25" stroke-linejoin="round"/>
         <path d="M2.8 12.2h5.2a1.25 1.25 0 010 2.5H2.8a1.25 1.25 0 010-2.5z" stroke="${STROKE}" stroke-width="1.2"/>
         <path d="M10.6 14.6 12.7 6.4 14.9 14.6z" stroke="${STROKE}" stroke-width="1.25" stroke-linejoin="round"/>
         <path d="M12.7 14.6v1.3" stroke="${STROKE}" stroke-width="1.25" stroke-linecap="round"/>`,
        size,
      )
    case 'backhoe':
      return svg(
        `<path d="M3.2 12.4V8h4.6v4.4" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M7.8 9.2 12 5.8l2.2 2.2-1.8 1.4" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
         <path d="M14.2 8l1.2 2.2H12.6" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <circle cx="4.4" cy="13.6" r="1.15" stroke="${STROKE}" stroke-width="1.2"/>
         <circle cx="7.2" cy="13.6" r="1.15" stroke="${STROKE}" stroke-width="1.2"/>`,
        size,
      )
    case 'boat':
      return svg(
        `<path d="M2.4 11.2h10.2L15.6 13.6H5.2L2.4 11.2z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M6.2 11.2V8.2h3.4v3" stroke="${STROKE}" stroke-width="1.25" stroke-linejoin="round"/>
         <path d="M7.9 8.2V4.6" stroke="${STROKE}" stroke-width="1.25" stroke-linecap="round"/>`,
        size,
      )
    case 'airplane':
      return mdiSvg(mdiAirplane, size)
    case 'helicopter':
      return mdiSvg(mdiHelicopter, size)
    case 'drone':
      return mdiSvg(mdiQuadcopter, size)
    case 'rollover_vehicle':
      return svg(
        `<g transform="rotate(-16 9 9)">
           <path d="M1.8 7.6h14.4v3.2H1.8z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
           <path d="M4.1 10.8 6.2 14.4h5.6l2.1-3.6" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
           <path d="M3.3 7.6A2.05 2.05 0 0 0 7.4 7.6" stroke="${STROKE}" stroke-width="1.35"/>
           <path d="M10.6 7.6A2.05 2.05 0 0 0 14.7 7.6" stroke="${STROKE}" stroke-width="1.35"/>
           <circle cx="5.35" cy="6.35" r="0.55" fill="${STROKE}"/>
           <circle cx="12.65" cy="6.35" r="0.55" fill="${STROKE}"/>
         </g>`,
        size,
      )
    case 'ems':
      return svg(
        `<path d="M1.8 12.4V3.2H12.2V12.4H1.8z" stroke="${STROKE}" stroke-width="1.35" stroke-linejoin="miter"/>
         <path d="M12.2 7.6 15.8 9.4V12.4" stroke="${STROKE}" stroke-width="1.35" stroke-linejoin="miter" stroke-linecap="square"/>
         <rect x="5.7" y="4.3" width="2.6" height="7" fill="${STROKE}"/>
         <rect x="3.5" y="6.5" width="7" height="2.6" fill="${STROKE}"/>
         <circle cx="4.6" cy="13.7" r="1.05" stroke="${STROKE}" stroke-width="1.15"/>
         <circle cx="10.6" cy="13.7" r="1.05" stroke="${STROKE}" stroke-width="1.15"/>`,
        size,
      )
    case 'police':
      return svg(
        `<path d="M9 2.8l5.2 2.2v4.2c0 3.2-2.2 5.2-5.2 6.2-3-1-5.2-3-5.2-6.2V5L9 2.8z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/><path d="M9 7.2v3.2M7.4 8.8h3.2" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>`,
        size,
      )
    case 'road': {
      if (variant === 'closed') {
        return svg(
          `<path d="M4.2 14.2 9 5.2l4.8 9" stroke="${STROKE}" stroke-width="1.4" stroke-linejoin="round" stroke-linecap="round"/>
           <path d="M5.5 10.4h7" stroke="${STROKE}" stroke-width="1.55" stroke-linecap="round"/>`,
          size,
        )
      }
      return svg(
        `<path d="M4 3.2v11.6M14 3.2v11.6M7.2 6h3.6M7.2 9h3.6M7.2 12h3.6" stroke="${STROKE}" stroke-width="1.4" stroke-linecap="round"/>`,
        size,
      )
    }
    case 'icp':
      return svg(
        `<path d="M5.4 14.6V3.4" stroke="${STROKE}" stroke-width="1.35" stroke-linecap="round"/>
         <path d="M5.4 3.6h6.4L10.2 6l1.6 2.4H5.4" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>`,
        size,
      )
    case 'incident_commander':
      return svg(
        `<path d="M9 2.6l1.6 3.3 3.6.5-2.6 2.6.6 3.6L9 10.8 5.8 12.6l.6-3.6-2.6-2.6 3.6-.5L9 2.6z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>`,
        size,
      )
    case 'dispatch':
      return svg(
        `<text x="9" y="12.6" text-anchor="middle" fill="${STROKE}" font-size="8.6" font-weight="800" font-family="ui-sans-serif,system-ui,sans-serif" letter-spacing="-0.45">911</text>`,
        size,
      )
    case 'responder':
      return svg(
        `<circle cx="9" cy="5.2" r="2.2" stroke="${STROKE}" stroke-width="1.3"/>
         <path d="M4.6 14.4c.6-3 2.2-4.4 4.4-4.4s3.8 1.4 4.4 4.4" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>`,
        size,
      )
    case 'operator':
      return mdiSvg(mdiAccountHardHat, size)
    case 'person':
      return mdiSvg(mdiAccount, size)
    case 'child':
      return mdiSvg(mdiHumanChild, size)
    case 'pipeline_personnel':
      return svg(
        `<path d="M6.2 5.8h5.6M6.8 5.8Q9 2.8 11.2 5.8" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>
         <path d="M4.6 14.6c.6-3 2.2-4.6 4.4-4.6s3.8 1.6 4.4 4.6" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>`,
        size,
      )
    case 'chief_officer':
      return svg(
        `<path d="M6.4 5.6h5.2M7.2 5.6V3.8h3.6v1.8" stroke="${STROKE}" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
         <circle cx="9" cy="6.6" r="1.7" stroke="${STROKE}" stroke-width="1.25"/>
         <path d="M4.6 14.6c.6-2.6 2.2-4 4.4-4s3.8 1.4 4.4 4" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>`,
        size,
      )
    case 'contractor':
      return svg(
        `<circle cx="9" cy="4.2" r="2" stroke="${STROKE}" stroke-width="1.25"/>
         <path d="M5.2 14.8 6.5 8.4h5l1.3 6.4" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M6.6 8.4 9 10.5 11.4 8.4" stroke="${STROKE}" stroke-width="1.2" stroke-linejoin="round"/>
         <path d="M6.8 11.2h4.4M7 12.7h4" stroke="${STROKE}" stroke-width="1.15" stroke-linecap="round"/>`,
        size,
      )
    case 'staging':
      return svg(
        `<path d="M3.4 6.2h11.2M3.4 9h11.2M3.4 11.8h11.2" stroke="${STROKE}" stroke-width="1.4" stroke-linecap="round"/>`,
        size,
      )
    case 'media_staging':
      return svg(
        `<rect x="2.8" y="6.6" width="8.2" height="5.4" rx="0.8" stroke="${STROKE}" stroke-width="1.3"/>
         <circle cx="6.8" cy="9.3" r="1.5" stroke="${STROKE}" stroke-width="1.2"/>
         <path d="M11 8.2 15.2 6.4v5.4L11 10.2" stroke="${STROKE}" stroke-width="1.25" stroke-linejoin="round"/>`,
        size,
      )
    case 'muster':
      return svg(
        `<circle cx="9" cy="9" r="5.2" stroke="${STROKE}" stroke-width="1.4"/><path d="M9 5.4v4.2l2.6 1.6" stroke="${STROKE}" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>`,
        size,
      )
    case 'access_point':
      return svg(
        `<path d="M3.2 5.4v8.2M14.8 5.4v8.2M3.2 7.4H6.6M3.2 10.8H6.6M11.4 7.4h3.4M11.4 10.8h3.4" stroke="${STROKE}" stroke-width="1.35" stroke-linecap="round"/>`,
        size,
      )
    case 'valve_open':
      return svg(
        `<path d="M1.2 11.2h15.6" stroke="${STROKE}" stroke-width="1.55" stroke-linecap="round"/>
         <path d="M4.6 8.4 9 11.2 4.6 14M13.4 8.4 9 11.2 13.4 14" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M9 8.4V5.2" stroke="${STROKE}" stroke-width="1.25" stroke-linecap="round"/>
         <ellipse cx="9" cy="4.4" rx="2.5" ry="1.15" stroke="${STROKE}" stroke-width="1.25"/>`,
        size,
      )
    case 'valve_closed':
      return svg(
        `<path d="M1.2 11.2h3.4M13.4 11.2h3.4" stroke="${STROKE}" stroke-width="1.55" stroke-linecap="round"/>
         <path d="M4.6 8.4 9 11.2 4.6 14M13.4 8.4 9 11.2 13.4 14" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M6.5 9.2 11.5 13.2M11.5 9.2 6.5 13.2" stroke="${STROKE}" stroke-width="1.45" stroke-linecap="round"/>
         <path d="M9 8.4V5.2" stroke="${STROKE}" stroke-width="1.25" stroke-linecap="round"/>
         <ellipse cx="9" cy="4.4" rx="2.5" ry="1.15" stroke="${STROKE}" stroke-width="1.25"/>`,
        size,
      )
    case 'pump':
      return svg(
        `<rect x="3.4" y="4.8" width="11.2" height="8.8" rx="1.1" stroke="${STROKE}" stroke-width="1.3"/>
         <circle cx="9" cy="9.2" r="2.2" stroke="${STROKE}" stroke-width="1.25"/>
         <path d="M8 4.8V3.4h2v1.4" stroke="${STROKE}" stroke-width="1.2" stroke-linecap="round"/>`,
        size,
      )
    case 'compressor':
      return svg(
        `<circle cx="5.2" cy="13.1" r="2.35" stroke="${STROKE}" stroke-width="1.25"/>
         <circle cx="5.2" cy="13.1" r="0.75" fill="${STROKE}"/>
         <rect x="2.6" y="6.4" width="12.8" height="5.8" rx="2.9" stroke="${STROKE}" stroke-width="1.3"/>
         <rect x="8.2" y="2.4" width="3.4" height="4" stroke="${STROKE}" stroke-width="1.2"/>
         <path d="M8.9 3.3h2M8.9 4.4h2M8.9 5.5h2" stroke="${STROKE}" stroke-width="0.9" stroke-linecap="round"/>
         <path d="M12.2 12.2h2.3v3.2h-2.3z" stroke="${STROKE}" stroke-width="1.2" stroke-linejoin="round"/>`,
        size,
      )
    case 'regulator_station':
      return svg(
        `<rect x="4" y="8" width="10" height="5.6" rx="1" stroke="${STROKE}" stroke-width="1.3"/>
         <path d="M9 8V5.4" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>
         <circle cx="9" cy="4.2" r="1.45" stroke="${STROKE}" stroke-width="1.25"/>`,
        size,
      )
    case 'city_gate':
      return svg(
        `<path d="M2.2 8.6h4.6M11.2 8.6h4.6M9 10.6v4.2" stroke="${STROKE}" stroke-width="1.35" stroke-linecap="round"/>
         <rect x="6.6" y="6.4" width="4.8" height="4.4" rx="0.6" stroke="${STROKE}" stroke-width="1.3"/>`,
        size,
      )
    case 'mercaptan_station':
      return svg(
        `<g transform="translate(-0.6 2.4) scale(0.56)">
           <path d="${mdiEgg}" fill="none" stroke="${STROKE}" stroke-width="2.15" stroke-linejoin="round"/>
         </g>
         <path d="M12.2 4.4c1-.7 2.2-.5 2.7.6M12.8 6.8c1.1-.5 2.3-.2 2.8.8M12 8.8c.9-.4 1.9 0 2.3.7" stroke="${STROKE}" stroke-width="1.1" stroke-linecap="round"/>`,
        size,
      )
    case 'meter_station':
      return svg(
        `<rect x="3.4" y="5.6" width="11.2" height="6.8" rx="1.1" stroke="${STROKE}" stroke-width="1.25"/>
         <text x="9" y="12.55" text-anchor="middle" fill="${STROKE}" font-size="10.5" font-weight="700" font-family="ui-sans-serif,system-ui,sans-serif">$</text>`,
        size,
      )
    case 'drawbridge':
      return svg(
        `<path d="M3.2 14.2V6.4h2.6v7.8M5.8 8.2 14.8 4.2M5.8 12.6H15.2" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>`,
        size,
      )
    case 'air_monitoring':
      return svg(
        `<path d="M4 12.6c1.2-2.4 2.6-4 5-4s3.8 1.6 5 4" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>
         <circle cx="9" cy="7.2" r="1.4" stroke="${STROKE}" stroke-width="1.3"/>`,
        size,
      )
    case 'hard_boom':
    case 'boom':
      return svg(
        `<path d="M2.6 9h12.8M6.2 7.4v3.2M11.4 7.4v3.2" stroke="${STROKE}" stroke-width="1.4" stroke-linecap="round"/>`,
        size,
      )
    case 'absorbent_boom':
      return svg(
        `<path d="M2.8 12.2Q9 5.6 15.2 11.4" stroke="${STROKE}" stroke-width="2.3" stroke-linecap="round"/>`,
        size,
      )
    case 'absorbent_pads':
      return svg(
        `<rect x="3.2" y="4.2" width="8" height="7.2" stroke="${STROKE}" stroke-width="1.3"/>
         <rect x="6.6" y="7.2" width="8" height="7.2" stroke="${STROKE}" stroke-width="1.3"/>`,
        size,
      )
    case 'foam':
      return svg(
        `<circle cx="6.2" cy="11" r="2.35" stroke="${STROKE}" stroke-width="1.3"/>
         <circle cx="11.6" cy="11.2" r="2.35" stroke="${STROKE}" stroke-width="1.3"/>
         <circle cx="8.8" cy="6.8" r="2.35" stroke="${STROKE}" stroke-width="1.3"/>`,
        size,
      )
    case 'temporary_mats':
      return svg(
        `<rect x="3.2" y="4.6" width="11.6" height="8.8" stroke="${STROKE}" stroke-width="1.3"/>
         <path d="M3.2 7.6h11.6M3.2 10.4h11.6M7.2 4.6v8.8M11 4.6v8.8" stroke="${STROKE}" stroke-width="1.15"/>`,
        size,
      )
    case 'underflow_dam':
      return svg(
        `<path d="M3.6 6h10.8M4.6 6v7.6M13.4 6v7.6M4.6 13.6h2.6M10.8 13.6h2.6M7.6 12.2h2.8" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>`,
        size,
      )
    case 'overflow_dam':
      return svg(
        `<path d="M4.4 8.2V13.8h9.2V8.2H4.4z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>
         <path d="M6 8.2Q9 4.4 12 8.2v1.8" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>`,
        size,
      )
    case 'dike':
      return svg(
        `<path d="M2.4 4.4 9 11.2l6.6-6.8" stroke="${STROKE}" stroke-width="1.4" stroke-linejoin="round" stroke-linecap="round"/>
         <path d="M2.2 4.4h13.6" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>`,
        size,
      )
    case 'restroom':
      return mdiSvg(mdiToilet, size)
    case 'porta_potty':
      return svg(
        `<path d="M11.6 2.5V1.15" stroke="${STROKE}" stroke-width="1.2" stroke-linecap="round"/>
         <rect x="5.3" y="2.5" width="7.4" height="12.6" rx="1.35" stroke="${STROKE}" stroke-width="1.25"/>
         <path d="M6.35 5.5h5.3v9.2h-5.3z" stroke="${STROKE}" stroke-width="1.1"/>
         <path d="M7.3 6.5h3.4M7.3 7.45h3.4" stroke="${STROKE}" stroke-width="0.85" stroke-linecap="round"/>
         <path d="M10.45 9.7v1.55" stroke="${STROKE}" stroke-width="1.25" stroke-linecap="round"/>
         <rect x="4.5" y="15.05" width="9" height="1.35" rx="0.25" stroke="${STROKE}" stroke-width="1.15"/>`,
        size,
      )
    case 'frac_tank':
      return svg(
        `<rect x="3.4" y="5.4" width="11.2" height="7.6" rx="1.4" stroke="${STROKE}" stroke-width="1.3"/>
         <path d="M6 5.4V4.2h6v1.2M5.2 9.2h7.6" stroke="${STROKE}" stroke-width="1.2" stroke-linecap="round"/>`,
        size,
      )
    case 'label':
      return svg(
        `<path d="M3.2 4.2h11.6M9 4.2v9.6M6 13.8h6" stroke="${STROKE}" stroke-width="1.4" stroke-linecap="round"/>`,
        size,
      )
    /** Timeline narration beat — not a palette / map type. Compact lines (quiet vs type icons). */
    case 'story_beat':
      return svg(
        `<path d="M5.5 6.2h7M5.5 9h7M5.5 11.8h4.5" stroke="${STROKE}" stroke-width="1.35" stroke-linecap="round"/>`,
        size,
      )
    case 'wind':
      return svg(
        `<path d="M3 7.2h7.2a1.8 1.8 0 000-3.6 1.8 1.8 0 00-1.6 1.2M3 10.2h9.4a2.1 2.1 0 010 4.2 2.1 2.1 0 01-2-1.6" stroke="${STROKE}" stroke-width="1.3" stroke-linecap="round"/>`,
        size,
      )
    case 'start':
      return svg(
        `<path d="M5.2 3.8v10.4L14.2 9 5.2 3.8z" stroke="${STROKE}" stroke-width="1.4" stroke-linejoin="round"/>`,
        size,
      )
    case 'school':
      return svg(
        `<path d="M3.2 8.2L9 4.4l5.8 3.8M5 8.6v5h8v-5" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>`,
        size,
      )
    case 'daycare':
      return svg(
        `<path d="M2.2 8.4 6.6 5.2l4.4 3.2M3.4 8.6v4.4h6.2V8.6" stroke="${STROKE}" stroke-width="1.25" stroke-linejoin="round"/>
         <circle cx="14" cy="8.8" r="1.35" stroke="${STROKE}" stroke-width="1.2"/>
         <path d="M12.2 13.6c.3-1.7 1-2.6 1.8-2.6s1.5.9 1.8 2.6" stroke="${STROKE}" stroke-width="1.2" stroke-linecap="round"/>`,
        size,
      )
    case 'hospital':
      return svg(
        `<rect x="3.4" y="3.4" width="11.2" height="11.2" rx="1.4" stroke="${STROKE}" stroke-width="1.3"/>
         <path d="M9 5.8v6.4M5.8 9h6.4" stroke="${STROKE}" stroke-width="1.5" stroke-linecap="round"/>`,
        size,
      )
    case 'water_treatment':
      return svg(
        `<path d="M9 3.4c2.8 3.2 4.6 5.4 4.6 7.4a4.6 4.6 0 11-9.2 0c0-2 1.8-4.2 4.6-7.4z" stroke="${STROKE}" stroke-width="1.3" stroke-linejoin="round"/>`,
        size,
      )
    default:
      return svg(`<circle cx="9" cy="9" r="5.2" stroke="${STROKE}" stroke-width="1.4"/>`, size)
  }
}

export function iconVariantFor(obj: {
  type: string
  effective: Record<string, unknown>
  params: Record<string, unknown>
}): string | undefined {
  if (obj.type === 'road') {
    return String(obj.effective.status ?? obj.params.status) === 'closed' ? 'closed' : 'open'
  }
  if (obj.type === 'drawbridge') {
    return String(obj.effective.deckStatus ?? obj.params.deckStatus)
  }
  return undefined
}
